// Onboarding "Navigator" (Oct 2026) — first-time player flow, presented as
// an illuminated parchment scroll. Welcome -> faction plaques -> first
// battle -> victory proclamation.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { FACTIONS, useFaction } from "@/contexts/FactionContext";
import "./Navigator.css";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LATER_KEY = "chess_navigator_later";
const FACTION_LORE = {
  "iron-kingdom": "Earth · Strength · The Bear",
  "dragonborn": "Fire · Attack · The Dragon",
  "elf-court": "Water · Defense · The Wolf",
  "celestial-host": "Air · Initiative · The Eagle",
};
function Corner({ pos }) {
  return (
    <svg className={`nav-corner ${pos}`} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={`navg-${pos}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFF2B0" />
          <stop offset="0.5" stopColor="#C99A3A" />
          <stop offset="1" stopColor="#7A5410" />
        </linearGradient>
      </defs>
      <g fill="none" stroke={`url(#navg-${pos})`} strokeWidth="1.8" strokeLinecap="round">
        <path d="M4 60 V14 Q4 4 14 4 H60" />
        <path d="M10 60 V18 Q10 10 18 10 H60" strokeWidth="1" />
        <path d="M14 14 C 24 6, 34 22, 24 28 C 16 33, 12 22, 20 20" />
        <path d="M28 8 C 34 2, 44 6, 42 12" />
        <path d="M8 28 C 2 34, 6 44, 12 42" />
        <circle cx="20" cy="20" r="2.2" fill="#C99A3A" stroke="none" />
      </g>
    </svg>
  );
}
function Scroll({ kicker, title, children }) {
  return (
    <div className="nav-scroll">
      <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
      {kicker && <p className="nav-kicker">{kicker}</p>}
      <DialogTitle className="nav-title">{title}</DialogTitle>
      <div className="nav-rule" aria-hidden="true">❦</div>
      {children}
    </div>
  );
}
export default function Navigator() {
  const { user, authEnabled } = useAuth();
  const { faction, setFaction } = useFaction();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("welcome"); // welcome | faction | battle | resume | reward
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reward, setReward] = useState(null);
  useEffect(() => {
    if (!user || !authEnabled) return;
    let later = false;
    try { later = window.sessionStorage.getItem(LATER_KEY) === "1"; } catch (e) { /* ignore */ }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/onboarding/status`, { withCredentials: true });
        if (cancelled || data.completed) return;
        if (data.game_id) {
          try {
            const r = await axios.post(`${API}/onboarding/complete`, {}, { withCredentials: true });
            if (cancelled) return;
            if (!r.data.already_completed) {
              setReward(r.data);
              setStep("reward");
              setOpen(true);
            }
          } catch (e) {
            if (cancelled || later) return;
            setStep("resume");
            setOpen(true);
          }
          return;
        }
        if (later) return;
        setStep("welcome");
        setOpen(true);
      } catch (e) { /* not signed in or endpoint unavailable — stay hidden */ }
    })();
    return () => { cancelled = true; };
  }, [user, authEnabled]);
  const closeForNow = () => {
    try { window.sessionStorage.setItem(LATER_KEY, "1"); } catch (e) { /* ignore */ }
    setOpen(false);
  };
  const pickFaction = async (value) => {
    setFaction(value);
    try { await axios.put(`${API}/profile/me`, { faction: value }, { withCredentials: true }); } catch (e) { /* local pledge still applies */ }
    setStep("battle");
  };
  const startBattle = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/onboarding/start`, {}, { withCredentials: true });
      setOpen(false);
      navigate(`/game/${data.game_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "The field could not be prepared. Try again.");
    }
    setBusy(false);
  };
  const resumeBattle = async () => {
    try {
      const { data } = await axios.get(`${API}/onboarding/status`, { withCredentials: true });
      if (data.game_id) { setOpen(false); navigate(`/game/${data.game_id}`); }
    } catch (e) { /* ignore */ }
  };
  const skip = async () => {
    try { await axios.post(`${API}/onboarding/skip`, {}, { withCredentials: true }); } catch (e) { /* ignore */ }
    setOpen(false);
  };
  if (!user || !authEnabled) return null;
  const chosen = FACTIONS.find((f) => f.value === faction);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeForNow(); }}>
      <DialogContent className="nav-dialog" data-testid="navigator-dialog">
        {step === "welcome" && (
          <Scroll kicker="By Decree of the Four Thrones" title="Hear Ye, Commander">
            <DialogDescription className="nav-text dropcap">
              The Great War has shattered the realm into four kingdoms. The iron-forged men of the
              Iron Kingdom, the fire-born horde of the Dragonborn, the moonlit Elf Court, and the
              winged legions of the Celestial Host each lay claim to the crown, and none will yield.
              Here the board is the battlefield, and every piece a soul sworn to your banner.
              Choose your allegiance, prove your command in your first battle, and the realm
              shall remember your name.
            </DialogDescription>
            <p className="nav-signoff">— The Herald of the Four Thrones</p>
            <div className="nav-seal" aria-hidden="true">4X</div>
            <div className="nav-actions">
              <button type="button" className="nav-btn nav-primary" data-testid="nav-begin" onClick={() => setStep("faction")}>Break the Seal</button>
              <button type="button" className="nav-link" data-testid="nav-skip" onClick={skip}>I know the ways of war — skip the tutorial</button>
            </div>
          </Scroll>
        )}
        {step === "faction" && (
          <Scroll kicker="The Oath of Allegiance" title="Swear Your Banner">
            <DialogDescription className="nav-text" style={{ textAlign: "center" }}>
              Four houses. Four elements. Four beasts of war. Lay your hand upon a plaque to swear
              your oath. Your banner may be changed later in your Profile.
            </DialogDescription>
            <div className="nav-factions">
              {FACTIONS.map((f) => (
                <button
                  type="button"
                  key={f.value}
                  className={`nav-faction${faction === f.value ? " selected" : ""}`}
                  style={{ "--nav-glow": f.glow }}
                  data-testid={`nav-faction-${f.value}`}
                  onClick={() => pickFaction(f.value)}
                >
                  {f.image && <img src={f.image} alt="" className="nav-faction-img" />}
                  <span className="nav-faction-name">{f.label}</span>
                  <span className="nav-faction-lore">{FACTION_LORE[f.value]}</span>
                  <span className="nav-faction-tag">{f.tagline}</span>
                </button>
              ))}
            </div>
          </Scroll>
        )}
        {step === "battle" && (
          <Scroll kicker={chosen ? `Sworn to the ${chosen.label}` : "Your Banner Is Raised"} title="Your First Battle">
            <DialogDescription className="nav-text dropcap">
              Across the board an untested rival awaits. This is a Classic duel: sixteen pieces a side,
              king against king. Checkmate the enemy king and the field is yours. Should the rules
              escape you in the heat of battle, seek the golden “?” upon the board and the Herald
              will remind you.
            </DialogDescription>
            <ul className="nav-spoils">
              <li>Gold for your coffers</li>
              <li>Twenty-five gems</li>
              <li>The title of Recruit</li>
              <li>Your first card: the heart of the poster</li>
            </ul>
            {error && <div className="nav-error">{error}</div>}
            <div className="nav-actions">
              <button type="button" className="nav-btn nav-primary" data-testid="nav-start-battle" disabled={busy} onClick={startBattle}>
                {busy ? "Preparing the field…" : "March to Battle"}
              </button>
              <button type="button" className="nav-link" onClick={skip}>Skip the tutorial</button>
            </div>
          </Scroll>
        )}
        {step === "resume" && (
          <Scroll kicker="A Summons" title="The Field Awaits">
            <DialogDescription className="nav-text dropcap">
              Your first battle stands unfinished, Commander. Return to the field and see it through;
              the spoils of twenty-five gems and the title of Recruit await the one who finishes what
              they begin.
            </DialogDescription>
            <div className="nav-actions">
              <button type="button" className="nav-btn nav-primary" data-testid="nav-resume" onClick={resumeBattle}>Return to Battle</button>
              <button type="button" className="nav-link" onClick={closeForNow}>Later</button>
            </div>
          </Scroll>
        )}
        {step === "reward" && reward && (
          <Scroll kicker="Proclamation" title={reward.won ? "Victory!" : "The Battle Is Done"}>
            <DialogDescription className="nav-text dropcap">
              {reward.won
                ? "Let it be known across the four kingdoms: the enemy king has fallen by your hand. "
                : "You held the field and saw the battle through, as a true commander must. "}
              By order of the Four Thrones you are hereby granted the title of <b>{reward.title}</b> and
              <b> {reward.gems_awarded} gems</b> for your war chest, and the first card of the poster is now yours.
              Every battle, order and muster feeds the Forge: collect all nine cards to reveal the Great War.
            </DialogDescription>
            <div className="nav-seal" aria-hidden="true">✦</div>
            <div className="nav-actions">
              <button type="button" className="nav-btn nav-primary" data-testid="nav-to-cards" onClick={() => { setOpen(false); navigate("/cards"); }}>Behold the Cards</button>
              <button type="button" className="nav-btn" data-testid="nav-to-games" onClick={() => { setOpen(false); navigate("/games"); }}>Seek Another Battle</button>
            </div>
          </Scroll>
        )}
      </DialogContent>
    </Dialog>
  );
}
